// Формати відображення (CONVENTIONS §3): дати ДД.ММ.РРРР, час ГГ:ХХ, тижні «Тиждень 5 (чисельник)».
// Навчальні дати приходять як 'YYYY-MM-DD' (@db.Date) — без зсуву часового поясу; timestamps — ISO UTC,
// показуються в Europe/Kyiv. Парність тижня (D8): непарний = чисельник (ODD), парний = знаменник (EVEN).
import { PARITY_LABELS, type Parity } from '@unisched/shared';

export const TIME_ZONE = 'Europe/Kyiv';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_ONLY = /^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/;

const pad = (n: number) => String(n).padStart(2, '0');

type DateInput = string | number | Date | null | undefined;

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const kyivParts = new Intl.DateTimeFormat('uk-UA', {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

function partsInKyiv(d: Date): { y: string; m: string; d: string; h: string; min: string } {
  const p = Object.fromEntries(kyivParts.formatToParts(d).map((x) => [x.type, x.value]));
  return { y: p.year, m: p.month, d: p.day, h: p.hour === '24' ? '00' : p.hour, min: p.minute };
}

/** Дата ДД.ММ.РРРР. 'YYYY-MM-DD' — як є (навчальна дата), інше — у часовому поясі Києва. */
export function formatDate(value: DateInput, empty = '—'): string {
  if (typeof value === 'string') {
    const m = DATE_ONLY.exec(value) ?? /^(\d{4})-(\d{2})-(\d{2})T00:00:00(?:\.000)?Z$/.exec(value);
    if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  }
  const d = toDate(value);
  if (!d) return empty;
  const p = partsInKyiv(d);
  return `${p.d}.${p.m}.${p.y}`;
}

/** Час ГГ:ХХ. 'HH:mm[:ss]' — як є, timestamp — у часовому поясі Києва. */
export function formatTime(value: DateInput, empty = '—'): string {
  if (typeof value === 'string') {
    const m = TIME_ONLY.exec(value.trim());
    if (m) return `${pad(Number(m[1]))}:${m[2]}`;
  }
  const d = toDate(value);
  if (!d) return empty;
  const p = partsInKyiv(d);
  return `${p.h}:${p.min}`;
}

/** Дата й час ДД.ММ.РРРР ГГ:ХХ (Europe/Kyiv). */
export function formatDateTime(value: DateInput, empty = '—'): string {
  const d = toDate(value);
  if (!d) return empty;
  const p = partsInKyiv(d);
  return `${p.d}.${p.m}.${p.y} ${p.h}:${p.min}`;
}

/** Діапазон дат «01.09.2026 – 31.12.2026». */
export function formatDateRange(from: DateInput, to: DateInput): string {
  const a = formatDate(from, '');
  const b = formatDate(to, '');
  if (a && b) return a === b ? a : `${a} – ${b}`;
  return a || b || '—';
}

/** Проміжок часу «09:15–10:50». */
export function formatTimeRange(from: DateInput, to: DateInput): string {
  return `${formatTime(from, '')}–${formatTime(to, '')}`;
}

/** Date → 'YYYY-MM-DD' (локальна календарна дата). */
export function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** 'YYYY-MM-DD' → Date (опівніч локального часу), без зсуву поясу. */
export function parseIsoDate(value: string): Date | null {
  const m = DATE_ONLY.exec(value);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** Сьогодні за Києвом, 'YYYY-MM-DD'. */
export function todayIso(): string {
  const p = partsInKyiv(new Date());
  return `${p.y}-${p.m}-${p.d}`;
}

/** 'ДД.ММ.РРРР' → 'YYYY-MM-DD' (null — не дата). */
export function parseUaDate(value: string): string | null {
  const m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(value.trim());
  if (!m) return null;
  const iso = `${m[3]}-${pad(Number(m[2]))}-${pad(Number(m[1]))}`;
  const d = parseIsoDate(iso);
  return d && toIsoDate(d) === iso ? iso : null;
}

// ---------------------------------------------------------------- тижні й дні

/** Парність номера тижня семестру: непарний — чисельник (ODD), парний — знаменник (EVEN). */
export function weekParity(week: number): Extract<Parity, 'ODD' | 'EVEN'> {
  return week % 2 === 1 ? 'ODD' : 'EVEN';
}

/** Підпис парності (з @unisched/shared): «Чисельник» / «Знаменник» / «Щотижня». */
export function parityLabel(parity: Parity): string {
  return PARITY_LABELS[parity];
}

/** «Тиждень 5 (чисельник)». */
export function formatWeek(week: number): string {
  return `Тиждень ${week} (${PARITY_LABELS[weekParity(week)].toLowerCase()})`;
}

/**
 * Номер тижня семестру (з 1) для дати: тиждень рахується від дати початку семестру (D8),
 * межі тижнів — понеділки. Дата до початку — 0 або від'ємне.
 */
export function semesterWeek(date: string | Date, semesterStart: string | Date): number {
  const d = typeof date === 'string' ? parseIsoDate(date) : date;
  const s = typeof semesterStart === 'string' ? parseIsoDate(semesterStart) : semesterStart;
  if (!d || !s) return 0;
  const monday = (x: Date) => {
    const r = new Date(x.getFullYear(), x.getMonth(), x.getDate());
    r.setDate(r.getDate() - ((r.getDay() + 6) % 7));
    return r;
  };
  const diffDays = Math.round((monday(d).getTime() - monday(s).getTime()) / 86_400_000);
  return Math.floor(diffDays / 7) + 1;
}

const WEEKDAYS_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'] as const;
const WEEKDAYS_LONG = ['Понеділок', 'Вівторок', 'Середа', 'Четвер', "П'ятниця", 'Субота', 'Неділя'] as const;

/** День тижня 1 = Пн … 7 = Нд (як weekday у схемі). */
export function weekdayLabel(weekday: number, style: 'short' | 'long' = 'short'): string {
  const list = style === 'short' ? WEEKDAYS_SHORT : WEEKDAYS_LONG;
  return list[weekday - 1] ?? String(weekday);
}

/** Номер пари римськими цифрами (I–VIII), як у розкладах ЛНМУ. */
export function pairRoman(index: number): string {
  return ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII'][index - 1] ?? String(index);
}

// ---------------------------------------------------------------- числа й слова

const intFmt = new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 0 });
const numFmt = new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 2 });

export function formatInt(n: number | null | undefined, empty = '—'): string {
  return n === null || n === undefined || Number.isNaN(n) ? empty : intFmt.format(n);
}

export function formatNumber(n: number | null | undefined, empty = '—'): string {
  return n === null || n === undefined || Number.isNaN(n) ? empty : numFmt.format(n);
}

/** Відсоток «42 %». */
export function formatPercent(n: number | null | undefined, empty = '—'): string {
  return n === null || n === undefined || Number.isNaN(n) ? empty : `${numFmt.format(n)} %`;
}

/** Українська множина: plural(5, ['запис', 'записи', 'записів']) → 'записів'. */
export function plural(n: number, forms: readonly [string, string, string]): string {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return forms[2];
  if (b > 1 && b < 5) return forms[1];
  if (b === 1) return forms[0];
  return forms[2];
}

/** «12 записів». */
export function countLabel(n: number, forms: readonly [string, string, string]): string {
  return `${formatInt(n)} ${plural(n, forms)}`;
}

/** Розмір файлу «1,2 МБ». */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${numFmt.format(bytes / 1024)} КБ`;
  return `${numFmt.format(bytes / 1024 / 1024)} МБ`;
}

/** Так / ні. */
export function formatBool(v: boolean | null | undefined, empty = '—'): string {
  if (v === null || v === undefined) return empty;
  return v ? 'Так' : 'Ні';
}
